import { formatMoney } from './money';
import { outfitPieces, outfitStatus, type OutfitStatus } from './outfit';
import type { Board, Item, Outfit } from './types';

export interface BoardContents<I> {
  outfits: Outfit[];
  /** Pieces saved to the board on their own. */
  items: I[];
  /** Every piece on the board once, from its outfits and on its own. */
  pieces: I[];
}

export function boardContents<I extends Pick<Item, 'id' | 'category'>>(
  board: Pick<Board, 'items' | 'outfits'>,
  outfits: Outfit[],
  items: I[],
): BoardContents<I> {
  const byId = new Map(items.map((i) => [i.id, i]));
  const outfitById = new Map(outfits.map((o) => [o.id, o]));
  const boardOutfits = board.outfits.map((id) => outfitById.get(id)).filter((o): o is Outfit => !!o);
  const loose = board.items.map((id) => byId.get(id)).filter((i): i is I => !!i);
  const seen = new Set<string>();
  const pieces: I[] = [];
  for (const p of [...boardOutfits.flatMap((o) => outfitPieces(o, items)), ...loose]) {
    if (!seen.has(p.id)) {
      seen.add(p.id);
      pieces.push(p);
    }
  }
  return { outfits: boardOutfits, items: loose, pieces };
}

/** One line for a board: "2 Outfits · 4 Teile · 389,00 € offen". */
export function boardMeta(c: BoardContents<unknown>, status: OutfitStatus, currency?: string): string {
  const parts: string[] = [];
  if (c.outfits.length) parts.push(`${c.outfits.length} ${c.outfits.length === 1 ? 'Outfit' : 'Outfits'}`);
  if (c.items.length) parts.push(`${c.items.length} ${c.items.length === 1 ? 'Teil' : 'Teile'}`);
  if (!parts.length) return 'Noch leer';
  if (status.missingCents) parts.push(`${formatMoney(status.missingCents, currency)} offen`);
  else if (status.complete) parts.push('alles gekauft');
  return parts.join(' · ');
}

export { outfitStatus as boardStatus };
